import { createReadStream } from 'node:fs';
import { createInterface } from 'node:readline';
import type {
  LogEntry,
  TraceBuilder,
  RecentTraceCallsResponse,
  LogFilePageResponse,
} from '../types';
import { fileExists, parseLogLine, extractNumber, matchesPath, matchesMethod } from '../utils';
import { readFileReverse, buildPaginatedResponse } from './file-reader';

/**
 * Read log entries by trace ID
 */
export async function readLogEntriesByTrace(
  filePath: string,
  traceId: string,
  limit: number,
): Promise<LogEntry[] | undefined> {
  const exists = await fileExists(filePath);
  if (!exists) {
    return undefined;
  }

  const matches: LogEntry[] = [];
  const stream = createReadStream(filePath, { encoding: 'utf8' });
  const rl = createInterface({ input: stream, crlfDelay: Infinity });

  for await (const line of rl) {
    const entry = parseLogLine(line);
    if (!entry) continue;
    if (entry.trace_id !== traceId) continue;

    matches.push(entry);
    if (limit > 0 && matches.length > limit) {
      matches.shift();
    }
  }

  rl.close();
  stream.close();

  return matches;
}

/**
 * Read recent trace calls with pagination
 */
export async function readRecentTraceCalls(
  filePath: string,
  page: number,
  pageSize: number,
  pathFilter?: string,
  methodFilter?: string,
): Promise<RecentTraceCallsResponse | undefined> {
  if (!(await fileExists(filePath))) {
    return undefined;
  }

  const config = {
    maxEntriesPerTrace: 10,
    chunkSize: 64 * 1024,
  };

  const builders = new Map<string, TraceBuilder>();
  const completedCalls: TraceBuilder[] = [];

  const createTraceBuilder = (traceId: string): TraceBuilder => ({
    traceId,
    entries: [],
    method: undefined,
    path: undefined,
    startTime: undefined,
    endTime: undefined,
    statusCode: undefined,
    durationMs: undefined,
    hasCompleted: false,
  });

  const updateBuilderMetadata = (builder: TraceBuilder, entry: LogEntry): void => {
    if (entry.method && !builder.method) builder.method = String(entry.method);
    if (entry.path && !builder.path) builder.path = String(entry.path);

    builder.entries.push(entry);
    if (builder.entries.length > config.maxEntriesPerTrace) {
      builder.entries.shift();
    }
  };

  const handleRequestCompleted = (builder: TraceBuilder, entry: LogEntry, message: string): void => {
    builder.hasCompleted = true;
    builder.endTime = entry.time;
    // 结构化日志将 status_code / duration_ms 作为顶层字段，优先读字段；
    // 仅当日志以文本形式拼接（例如 app.log 的 HTTP request failed 消息）时才回退正则。
    builder.statusCode =
      typeof entry.status_code === 'number' ? entry.status_code : extractNumber(message, /status_code:\s*(\d+)/);
    builder.durationMs =
      typeof entry.duration_ms === 'number' ? entry.duration_ms : extractNumber(message, /duration_ms:\s*(\d+)/);
    if (!builder.path && entry.path) {
      builder.path = String(entry.path);
    }

    const pathMatches = !pathFilter || matchesPath(builder.path, pathFilter);
    const methodMatches = !methodFilter || matchesMethod(builder.method, methodFilter);
    const shouldInclude = pathMatches && methodMatches;

    if (shouldInclude) {
      completedCalls.push(builder);
    }
  };

  const processLogEntry = (entry: LogEntry): void => {
    const { trace_id: traceId, message = '' } = entry;
    if (!traceId) return;

    let builder = builders.get(traceId);
    if (!builder) {
      builder = createTraceBuilder(traceId);
      builders.set(traceId, builder);
    }

    updateBuilderMetadata(builder, entry);

    if (!builder.hasCompleted && (message.includes('HTTP request completed') || message.includes('HTTP request failed'))) {
      handleRequestCompleted(builder, entry, message);
    }

    if (message.includes('HTTP request started') && !builder.startTime) {
      builder.startTime = entry.time;
    }
  };

  const processLine = (line: string): void => {
    const entry = parseLogLine(line);
    if (entry?.trace_id) {
      processLogEntry(entry);
    }
  };

  await readFileReverse(filePath, config.chunkSize, processLine);

  return buildPaginatedResponse(completedCalls, page, pageSize);
}

/**
 * Read log file page with pagination
 */
export async function readLogFilePage(
  filePath: string,
  page: number,
  pageSize: number,
): Promise<LogFilePageResponse | undefined> {
  if (!(await fileExists(filePath))) {
    return undefined;
  }

  const capacity = page * pageSize;
  const buffer: string[] = [];
  let totalLines = 0;

  const stream = createReadStream(filePath, { encoding: 'utf8' });
  const rl = createInterface({ input: stream, crlfDelay: Infinity });

  try {
    for await (const line of rl) {
      buffer.push(line);
      if (buffer.length > capacity) {
        buffer.shift();
      }
      totalLines += 1;
    }
  } finally {
    rl.close();
    stream.close();
  }

  const totalPages = totalLines === 0 ? 0 : Math.ceil(totalLines / pageSize);

  if (buffer.length === 0) {
    return { page, pageSize, totalLines, totalPages, lines: [] };
  }

  const startIndex = Math.max(totalLines - page * pageSize, 0);
  const endIndex = Math.max(totalLines - (page - 1) * pageSize, 0);
  const bufferStartIndex = totalLines - buffer.length;

  const lines: string[] = [];
  for (let i = buffer.length - 1; i >= 0; i -= 1) {
    const lineIndex = bufferStartIndex + i;
    if (lineIndex >= startIndex && lineIndex < endIndex) {
      lines.push(buffer[i]);
    }
  }

  return {
    page,
    pageSize,
    totalLines,
    totalPages,
    lines: lines.reverse(),
  };
}
